import { useEffect, useState } from 'react';
import { Search, Play, VideoOff, Dumbbell, Utensils, LayoutTemplate, Pencil } from 'lucide-react';
import { api, errMsg } from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import CoachShell from '@/components/CoachShell';
import { LoadingScreen } from '@/components/State';
import { toast } from 'sonner';
import { isValidYouTube } from '@/lib/youtube';
import VideoPlayerModal from '@/components/VideoPlayerModal';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

export default function Library() {
  const { t, tplName, catLabel } = useI18n();
  const [tab, setTab] = useState('exercises');
  const [loading, setLoading] = useState(true);
  const [exercises, setExercises] = useState([]);
  const [meals, setMeals] = useState([]);
  const [nutTpls, setNutTpls] = useState([]);
  const [trainTpls, setTrainTpls] = useState([]);
  const [q, setQ] = useState('');
  const [group, setGroup] = useState('all');
  const [videoEx, setVideoEx] = useState(null);
  const [editEx, setEditEx] = useState(null);
  const [videoInput, setVideoInput] = useState('');
  const [savingVideo, setSavingVideo] = useState(false);

  const openVideoEdit = (e) => { setEditEx(e); setVideoInput(e.video_url || ''); };

  const saveVideo = async (remove) => {
    if (!editEx) return;
    const url = remove ? '' : videoInput.trim();
    if (!remove) {
      if (/^\s*(javascript|data|vbscript):/i.test(url) || !url || !isValidYouTube(url)) {
        toast.error(t('lib.invalidVideoUrl'));
        return;
      }
    }
    setSavingVideo(true);
    try {
      const res = await api.put(`/exercise-library/${editEx.id}`, { video_url: url });
      const updated = res.data || { ...editEx, video_url: url };
      setExercises((prev) => prev.map((x) => (x.id === editEx.id ? { ...x, ...updated } : x)));
      toast.success(remove ? t('lib.videoRemoved') : t('lib.videoSaved'));
      setEditEx(null);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSavingVideo(false); }
  };

  useEffect(() => {
    (async () => {
      try {
        const [ex, ml, nt, tt] = await Promise.all([
          api.get('/exercise-library'),
          api.get('/meal-library').catch(() => ({ data: [] })),
          api.get('/nutrition-templates').catch(() => ({ data: [] })),
          api.get('/plan-templates').catch(() => ({ data: [] })),
        ]);
        setExercises(ex.data?.exercises || ex.data || []);
        setMeals(ml.data?.meals || ml.data || []);
        setNutTpls(nt.data?.templates || nt.data || []);
        setTrainTpls(tt.data?.templates || tt.data || []);
      } catch (e) { toast.error(errMsg(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  const groups = ['all', ...Array.from(new Set(exercises.map((e) => e.muscle_group).filter(Boolean)))];
  const exFiltered = exercises.filter((e) => (group === 'all' || e.muscle_group === group) && (e.name || '').toLowerCase().includes(q.toLowerCase()));
  const mlFiltered = meals.filter((m) => (m.name || '').toLowerCase().includes(q.toLowerCase()));

  const tabs = [
    { key: 'exercises', icon: Dumbbell, label: t('lib.tabExercises'), count: exercises.length },
    { key: 'meals', icon: Utensils, label: t('lib.tabMeals'), count: meals.length },
    { key: 'templates', icon: LayoutTemplate, label: t('lib.tabTemplates'), count: nutTpls.length + trainTpls.length },
  ];

  return (
    <CoachShell title={t('coach.library')}>
      {loading ? <LoadingScreen /> : (
        <>
          <div className="mb-5 flex gap-2">
            {tabs.map((tb) => (
              <button key={tb.key} onClick={() => { setTab(tb.key); setQ(''); }} data-testid={`libtab-${tb.key}`}
                className={`as-chip ${tab === tb.key ? 'border-asgym-accent bg-asgym-accent/15 text-asgym-accent' : 'border-white/[0.08] bg-white/[0.03] text-asgym-sub'}`}>
                <tb.icon size={13} /> {tb.label} <span className="opacity-60">{tb.count}</span>
              </button>
            ))}
          </div>

          {(tab === 'exercises' || tab === 'meals') && (
            <div className="mb-4 flex items-center gap-2 rounded-xl border border-white/[0.06] bg-asgym-surface px-3">
              <Search size={16} className="text-asgym-muted" />
              <input className="h-11 flex-1 bg-transparent text-sm text-white placeholder:text-asgym-muted outline-none" placeholder={t('picker.search')} value={q} onChange={(e) => setQ(e.target.value)} data-testid="lib-search" />
            </div>
          )}

          {tab === 'exercises' && (
            <>
              <div className="mb-4 flex flex-wrap gap-2">
                {groups.map((g) => (
                  <button key={g} onClick={() => setGroup(g)} className={`as-chip capitalize ${group === g ? 'border-asgym-accent bg-asgym-accent/15 text-asgym-accent' : 'border-white/[0.08] bg-white/[0.03] text-asgym-sub'}`}>{g}</button>
                ))}
              </div>
              <div className="grid gap-2.5 sm:grid-cols-2">
                {exFiltered.map((e) => {
                  const hasVideo = isValidYouTube(e.video_url);
                  return (
                    <div key={e.id} className="flex items-center justify-between gap-3 rounded-xl border border-white/[0.06] bg-asgym-surface p-3.5" data-testid="exercise-lib-item">
                      <div className="min-w-0">
                        <p className="truncate text-[14px] font-bold text-white">{e.name}</p>
                        <p className="text-[11px] text-asgym-muted capitalize">{e.muscle_group} · {e.device}</p>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                      {hasVideo ? (
                        <button
                          onClick={() => setVideoEx(e)}
                          data-testid={`lib-play-${e.id}`}
                          title={t('lib.videoAvailable')}
                          className="flex h-9 shrink-0 items-center gap-1.5 rounded-lg border border-asgym-accent/40 bg-asgym-accent/10 px-3 text-[11px] font-bold text-asgym-accent transition-transform active:scale-95"
                        >
                          <Play size={14} fill="currentColor" /> {t('lib.video')}
                        </button>
                      ) : (
                        <span className="flex shrink-0 items-center gap-1.5 text-[11px] font-semibold text-asgym-muted" data-testid={`lib-novideo-${e.id}`} title={t('lib.videoMissing')}>
                          <VideoOff size={15} />
                        </span>
                      )}
                      <button
                        onClick={() => openVideoEdit(e)}
                        data-testid={`lib-editvideo-${e.id}`}
                        title={t('lib.editVideoBtn')}
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-white/10 bg-white/[0.03] text-asgym-sub transition-transform hover:text-white active:scale-95"
                      >
                        <Pencil size={14} />
                      </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {tab === 'meals' && (
            <div className="grid gap-2.5 sm:grid-cols-2">
              {mlFiltered.map((m) => (
                <div key={m.id} className="rounded-xl border border-white/[0.06] bg-asgym-surface p-3.5" data-testid="meal-lib-item">
                  <div className="flex items-center justify-between">
                    <p className="truncate text-[14px] font-bold text-white">{m.name}</p>
                    <span className="shrink-0 text-[11px] font-bold text-asgym-accent">{m.calories || 0} kcal</span>
                  </div>
                  <p className="mt-0.5 text-[11px] text-asgym-muted">P {m.protein || 0} · C {m.carbs || 0} · F {m.fat || 0} · {m.category}</p>
                </div>
              ))}
            </div>
          )}

          {tab === 'templates' && (
            <div className="space-y-6">
              <div>
                <p className="as-section mb-3">{t('lib.trainingTemplates')}</p>
                <div className="grid gap-2.5 sm:grid-cols-2">
                  {trainTpls.map((tp) => (
                    <div key={tp.id} className="rounded-xl border border-white/[0.06] bg-asgym-surface p-4" data-testid="train-tpl-item">
                      <p className="text-[14px] font-bold text-white">{tp.name}</p>
                      <p className="mt-0.5 text-[11px] text-asgym-muted">{catLabel(tp.category)} · {tp.program_weeks || 1}W · {tp.days_per_week || (tp.weeks?.[0]?.days?.length) || '—'} {t('coach.days')}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <p className="as-section mb-3">{t('lib.nutritionTemplates')}</p>
                <div className="grid gap-2.5 sm:grid-cols-2">
                  {nutTpls.map((tp) => (
                    <div key={tp.id} className="rounded-xl border border-white/[0.06] bg-asgym-surface p-4" data-testid="nut-tpl-item">
                      <p className="text-[14px] font-bold text-white">{tplName(tp)}</p>
                      <p className="mt-0.5 text-[11px] text-asgym-muted">{catLabel(tp.category)}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </>
      )}
      <VideoPlayerModal open={!!videoEx} onOpenChange={(o) => !o && setVideoEx(null)} url={videoEx?.video_url} title={videoEx?.name} />
      <Dialog open={!!editEx} onOpenChange={(o) => !o && setEditEx(null)}>
        <DialogContent className="border-white/10 bg-asgym-surface text-asgym-text sm:max-w-md" data-testid="video-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-extrabold tracking-tight text-white">{t('lib.editVideo')}</DialogTitle>
            <DialogDescription className="text-asgym-sub">{editEx?.name}</DialogDescription>
          </DialogHeader>
          <input
            value={videoInput}
            onChange={(e) => setVideoInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') saveVideo(false); }}
            placeholder={t('lib.videoUrlPlaceholder')}
            data-testid="video-url-input"
            className="h-11 w-full rounded-xl border border-white/10 bg-asgym-bg px-3 text-sm text-white placeholder:text-asgym-muted outline-none focus:border-asgym-accent/50"
            autoFocus
          />
          <div className="mt-4 flex items-center justify-between gap-2">
            {isValidYouTube(editEx?.video_url) ? (
              <button onClick={() => saveVideo(true)} disabled={savingVideo} data-testid="video-remove-btn"
                className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-[12px] font-bold text-red-400 transition-transform active:scale-95 disabled:opacity-50">
                {t('lib.removeVideo')}
              </button>
            ) : <span />}
            <div className="flex gap-2">
              <button onClick={() => setEditEx(null)} data-testid="video-cancel-btn"
                className="rounded-lg border border-white/10 bg-white/[0.03] px-4 py-2 text-[12px] font-bold text-asgym-sub transition-transform active:scale-95">
                {t('common.cancel')}
              </button>
              <button onClick={() => saveVideo(false)} disabled={savingVideo} data-testid="video-save-btn"
                className="rounded-lg border border-asgym-accent/40 bg-asgym-accent/15 px-4 py-2 text-[12px] font-bold text-asgym-accent transition-transform active:scale-95 disabled:opacity-50">
                {t('common.save')}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </CoachShell>
  );
}
